import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useUser } from '../../../contexts/UserContext';
import { dmarcApi, fmtNumber, fmtPct, SourceIp } from '../../../services/dmarcApi';
import SourceKindChip from './SourceKindChip';

const Sources: React.FC = () => {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [sources, setSources] = useState<SourceIp[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin()) navigate('/app/welcome');
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin()) return;
    setSources(null);
    dmarcApi
      .listSources(days, 100)
      .then(r => setSources(r.sources))
      .catch(e => setError(e instanceof Error ? e.message : 'load failed'));
  }, [days, isAdmin]);

  if (!isAdmin()) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        component={RouterLink}
        to="/app/admin/dmarc"
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
      >
        Dashboard
      </Button>

      <Typography variant="h4" component="h1" fontWeight={600}>
        Global source IPs
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Aggregate of source IPs across all monitored domains.
      </Typography>

      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Typography variant="body2" color="text.secondary">
          Window:
        </Typography>
        <ButtonGroup size="small">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              variant={days === d ? 'contained' : 'outlined'}
              onClick={() => setDays(d)}
            >
              {d}d
            </Button>
          ))}
        </ButtonGroup>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load sources: {error}</Alert>}

      <Card>
        <CardContent>
          {!sources && !error && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          )}
          {sources && sources.length === 0 && (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No source IPs seen in this window.
            </Typography>
          )}
          {sources && sources.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Source IP</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell align="right">Messages</TableCell>
                    <TableCell align="right">Pass</TableCell>
                    <TableCell align="right">Fail</TableCell>
                    <TableCell align="right">Pass rate</TableCell>
                    <TableCell align="right">Domains</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sources.map(row => (
                    <TableRow hover key={row.source_ip}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {row.source_ip}
                      </TableCell>
                      <TableCell>
                        <SourceKindChip kind={row.source_kind} />
                      </TableCell>
                      <TableCell align="right">{fmtNumber(row.messages)}</TableCell>
                      <TableCell align="right">{fmtNumber(row.pass_dmarc)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          component="span"
                          sx={{ color: row.fail > 0 ? 'error.main' : 'text.primary' }}
                        >
                          {fmtNumber(row.fail)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum"' }}>
                        {fmtPct(row.pass_rate)}
                      </TableCell>
                      <TableCell align="right">
                        {row.domains && row.domains.length > 0 ? (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            flexWrap="wrap"
                          >
                            {row.domains.slice(0, 3).map(d => (
                              <Chip key={d} size="small" variant="outlined" label={d} />
                            ))}
                            {row.domains.length > 3 && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`+${row.domains.length - 3}`}
                              />
                            )}
                          </Stack>
                        ) : (
                          <Typography color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Sources;
